import MaterialIcon from '@components/common/MaterialIcon';
import { Spinner } from '@components/common/Spinner';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { IntentRedirectLabel } from '@components/train-ai/IntentRedirectLabel';
import { TRAIN_AI_PAGE_ROUTE } from '@constants/routes';
import type {
  IntelligenceQuestionsResponseDTO,
  IntelligenceTopicDTO,
} from '@conversed/rest/src/contracts/home/home.dto';
import type { Sentiment } from '@conversed/shared/src/central/constants/sentiment';
import type { TimeWindow } from '@conversed/shared/src/central/types/time-window.type';
import { purify } from '@helpers/domHelper';
import { useTopicQuestions } from '@hooks/home/useTopicQuestions';
import { useModal } from '@hooks/modal/useModal';
import { useHasTrainAIAccess } from '@hooks/useHasTrainAIAccess';
import parse from 'html-react-parser';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { translateSentimentInSentence } from '../sentimentDisplay';
import { SentimentChip } from './SentimentChip';
import { SentimentCounts, SentimentSplitBar } from './SentimentSplitBar';

export type TIntelligenceTopicModalProps = {
  topic: IntelligenceTopicDTO;
  timeWindow: TimeWindow;
  // The unfiltered first page, loaded before the modal opened.
  initialPage: IntelligenceQuestionsResponseDTO;
};

// * Loads the next page as soon as the end of the list scrolls into view. The
// * observer's root is the list itself, because the list - not the page - scrolls.
const useLoadMoreOnScroll = (
  hasMore: boolean,
  isLoading: boolean,
  hasError: boolean,
  listVersion: number,
  loadMore: () => void,
) => {
  const listRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    // * After a failure the sentinel stays visible; observing it again would
    // * re-request the failed page at once, so loading waits for a retry.
    if (!hasMore || isLoading || hasError || !sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      { root: listRef.current, rootMargin: '0px 0px 120px 0px' },
    );
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
    // * listVersion: a swap remounts the list, so the observer must be rebuilt
    // * with the new element as its root.
  }, [hasMore, isLoading, hasError, listVersion, loadMore]);

  return { listRef, sentinelRef };
};

export const IntelligenceTopicModal: React.FC<TIntelligenceTopicModalProps> = ({
  topic,
  timeWindow,
  initialPage,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hideModal } = useModal();
  const showTrainAILink = useHasTrainAIAccess();
  const [selectedSentiment, setSelectedSentiment] = useState<Sentiment | null>(
    null,
  );
  const selectedBucketCount = selectedSentiment
    ? topic.sentiment[selectedSentiment]
    : topic.volume;
  const {
    questions,
    hasMore,
    isLoading,
    hasError,
    isReplacing,
    listVersion,
    loadMore,
    retry,
  } = useTopicQuestions({
    topicId: topic.id,
    timeWindow,
    sentiment: selectedSentiment,
    selectedBucketCount,
    initialPage,
  });
  const { listRef, sentinelRef } = useLoadMoreOnScroll(
    hasMore,
    isLoading,
    hasError,
    listVersion,
    loadMore,
  );

  const goToTrainAI = () => {
    hideModal();
    navigate(TRAIN_AI_PAGE_ROUTE);
  };

  // Clicking the selected bucket again clears the filter.
  const toggleSentiment = (sentiment: Sentiment) => {
    setSelectedSentiment((current) =>
      current === sentiment ? null : sentiment,
    );
  };

  const levelLabel = (sentiment: Sentiment) =>
    translateSentimentInSentence(t, sentiment);

  const renderEmptyState = () => {
    if (selectedSentiment) {
      return t('home.v2.intelligence.topic-modal.no-filtered-questions', {
        level: levelLabel(selectedSentiment),
      });
    }
    return t('home.v2.intelligence.topic-modal.no-questions');
  };

  // * The empty-state copy claims the bucket has no questions, so it shows only
  // * when that is known: a zero count, or a successful empty response - never
  // * after a failed request.
  const isKnownEmpty =
    selectedSentiment !== null && topic.sentiment[selectedSentiment] === 0;
  const showEmptyState =
    isKnownEmpty || (!isLoading && !hasError && questions.length === 0);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-start gap-8'>
        <div className='flex flex-col'>
          <span className='text-pico font-medium uppercase tracking-wider text-gray-400'>
            {t('home.v2.intelligence.topic-modal.total-questions')}
          </span>
          <span className='text-3xl font-bold text-gray-800'>
            {topic.volume}
          </span>
        </div>

        <div className='flex flex-1 flex-col gap-1.5'>
          <span className='text-pico font-medium uppercase tracking-wider text-gray-400'>
            {t('home.v2.intelligence.topic-modal.sentiment')}
          </span>
          <SentimentSplitBar
            sentiment={topic.sentiment}
            selected={selectedSentiment}
            onSelect={toggleSentiment}
          />
          <SentimentCounts
            sentiment={topic.sentiment}
            selected={selectedSentiment}
            onSelect={toggleSentiment}
          />
        </div>
      </div>

      <div className='border-t border-gray-200 pt-4'>
        <div className='mb-3 flex items-center gap-2'>
          <p className='m-0 text-sm font-medium text-gray-700'>
            {t('home.v2.intelligence.topic-modal.questions-label')}
          </p>
          {selectedSentiment && (
            <span className='intelligence__filter-chip'>
              {t('home.v2.intelligence.topic-modal.filter-chip', {
                level: levelLabel(selectedSentiment),
                count: topic.sentiment[selectedSentiment],
              })}
              <button
                type='button'
                aria-label={t('home.v2.intelligence.topic-modal.clear-filter')}
                onClick={() => setSelectedSentiment(null)}
                className='flex items-center'>
                <MaterialIcon
                  icon='close'
                  classes={['text-tiny', '!leading-none']}
                />
              </button>
            </span>
          )}
        </div>

        {/* Always mounted so screen readers announce the text when it changes. */}
        <span className='sr-only' role='status'>
          {isReplacing ? t('home.v2.intelligence.topic-modal.filtering') : ''}
        </span>

        {showEmptyState ? (
          <p className='py-6 text-center text-sm text-gray-400'>
            {renderEmptyState()}
          </p>
        ) : (
          <div
            className={`relative${
              isReplacing && questions.length === 0
                ? ' intelligence__question-list-placeholder'
                : ''
            }`}>
            {isReplacing && (
              <div className='intelligence__list-loading'>
                <span className='intelligence__list-loading-text'>
                  {t('home.v2.intelligence.topic-modal.filtering')}
                </span>
                <Spinner size='md' />
              </div>
            )}
            <ul
              // * A new key per swap remounts the list: the fade-in replays and the
              // * scroll position starts at the top again.
              key={listVersion}
              ref={listRef}
              aria-busy={isReplacing}
              className={`intelligence__question-list${
                isReplacing ? ' intelligence__question-list--replacing' : ''
              }`}>
              {questions.map((question) => (
                <li
                  key={question._id}
                  className='flex flex-col gap-0.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5'>
                  <div className='flex items-start justify-between gap-3'>
                    <span className='flex items-start gap-2'>
                      <MaterialIcon
                        icon='person'
                        type='outlined'
                        classes={[
                          'text-sm',
                          'text-gray-400',
                          'mt-0.5',
                          '!leading-none',
                        ]}
                      />
                      <span className='text-tiny text-gray-700'>
                        {question.question}
                      </span>
                    </span>
                    <SentimentChip sentiment={question.sentiment} size='sm' />
                  </div>
                  {question.isIntentRedirect && <IntentRedirectLabel />}
                  {!question.isIntentRedirect && question.answer && (
                    <div className='flex items-start gap-2'>
                      <MaterialIcon
                        icon='smart_toy'
                        type='outlined'
                        classes={[
                          'text-sm',
                          'text-primary-base',
                          'mt-0.5',
                          '!leading-none',
                        ]}
                      />
                      <span className='text-tiny text-gray-400 formatted-html-content'>
                        {parse(purify(question.answer))}
                      </span>
                    </div>
                  )}
                </li>
              ))}
              {hasError && (
                <li className='flex items-center justify-center gap-2 py-2 text-tiny text-gray-400'>
                  {t('home.v2.intelligence.topic-modal.load-failed')}
                  <button
                    type='button'
                    onClick={retry}
                    className='font-medium text-primary-base hover:text-primary-dark'>
                    {t('home.v2.intelligence.topic-modal.retry')}
                  </button>
                </li>
              )}
              {!hasError && !isReplacing && (hasMore || isLoading) && (
                <li
                  ref={sentinelRef}
                  className='flex items-center justify-center gap-2 py-2 text-tiny text-gray-400'>
                  <Spinner size='sm' />
                  {t('home.v2.intelligence.topic-modal.loading-more')}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className='flex items-center justify-between border-t border-gray-200 pt-4'>
        <ButtonT
          type='button'
          styling='outline'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.close')}
        </ButtonT>
        {showTrainAILink && (
          <ButtonT
            type='button'
            styling='primary'
            size='xs'
            rounding='md'
            onClick={goToTrainAI}>
            <span className='flex items-center gap-1'>
              {t('home.v2.intelligence.topic-modal.train-ai')}
              <MaterialIcon icon='arrow_forward' classes={['text-sm']} />
            </span>
          </ButtonT>
        )}
      </div>
    </div>
  );
};
